import CustomDatePicker from '@/components/shared/CustomDatePicker';
import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useQuery } from "@tanstack/react-query";
import SearchableCombobox from "@/components/ui/SearchableCombobox";
import { ROLE_ORDER } from "@/lib/AuthSession";
import { Camera, CheckCircle2 } from "lucide-react";
import FaceRegistrationModal from "../attendance/FaceRegistrationModal";

export default function AddMemberDialog({ open, onClose, onSaved }) {
  const [form, setForm] = useState({
    full_name: "", email: "", password: "", phone: "", employee_id: "", nik: "",
    department: "", role: "", status: "aktif", gender: "pria",
    join_date: new Date().toISOString().split("T")[0], birth_date: "", notes: "", require_attendance: true
  });
  const [loading, setLoading] = useState(false);
  const [isFaceModalOpen, setIsFaceModalOpen] = useState(false);

  const { data: teamMembers = [] } = useQuery({
    queryKey: ["team-members"],
    queryFn: () => base44.entities.TeamMember.list(),
  });

  const uniqueDivisions = Array.from(new Set(teamMembers.map(m => m.department).filter(Boolean))).filter(d => d !== "Direktur Utama" && d !== "Developer").sort();
  const uniqueRoles = Array.from(new Set([...ROLE_ORDER, ...teamMembers.map(m => m.role)])).filter(r => r !== "owner" && r !== "developer" && r).sort();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.full_name || !form.email || !form.password) return;
    setLoading(true);
    await base44.entities.TeamMember.create(form);
    setLoading(false);
    setForm({
      full_name: "", email: "", password: "", phone: "", employee_id: "", nik: "",
      department: "", role: "", status: "aktif", gender: "pria",
      join_date: new Date().toISOString().split("T")[0], birth_date: "", notes: "", require_attendance: true, face_descriptor: null
    });
    onSaved?.();
    onClose();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Tambah Anggota Tim</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1 col-span-2">
                <Label className="text-xs">Nama Lengkap *</Label>
                <Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Password *</Label>
                <Input value={form.password} onChange={(e) => set("password", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">No. Telepon</Label>
                <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Employee ID</Label>
                <Input value={form.employee_id} onChange={(e) => set("employee_id", e.target.value)} className="font-mono" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">NIK</Label>
                <Input value={form.nik || ""} onChange={(e) => set("nik", e.target.value)} className="font-mono" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Divisi</Label>
                <SearchableCombobox
                  value={form.department}
                  onValueChange={(v) => set("department", v)}
                  options={uniqueDivisions}
                  placeholder="Pilih atau ketik divisi..."
                  searchPlaceholder="Cari divisi..."
                  allowCustom={true}
                />
              </div>

              {/* Jika Divisi Produksi: Tampilkan pilihan Primer vs Sekunder */}
              {(form.department === "Produksi" || form.department?.toLowerCase().includes("produksi")) && (
                <div className="space-y-1 col-span-2 bg-amber-50/70 border border-amber-200 p-2.5 rounded-xl">
                  <Label className="text-xs font-bold text-amber-900 flex items-center justify-between">
                    <span>Tipe Produksi (Primer / Sekunder):</span>
                    <span className="text-[10px] text-amber-700 font-normal">*Primer = Produksi, Sekunder = Pengemasan</span>
                  </Label>
                  <Select
                    value={form.sub_divisi || (form.position?.toLowerCase().includes('pengemasan') || form.role?.toLowerCase().includes('pengemasan') ? "sekunder" : "primer")}
                    onValueChange={(v) => {
                      set("sub_divisi", v);
                      if (v === "primer") {
                        if (!form.position || form.position.includes('Pengemasan')) set("position", "Staff Produksi (Primer)");
                        if (!form.role || form.role.includes('Pengemasan')) set("role", "Staff Produksi");
                      } else if (v === "sekunder") {
                        set("position", "Staff Pengemasan (Sekunder)");
                        set("role", "Staff Pengemasan");
                      }
                    }}
                  >
                    <SelectTrigger className="bg-white border-amber-300 text-xs h-9">
                      <SelectValue placeholder="-- Pilih Tipe Produksi --" />
                    </SelectTrigger>
                    <SelectContent className="z-[100]">
                      <SelectItem value="primer" className="text-xs">
                        🔵 Produksi Primer (Mixing, Filling, Maserasi, Bulk)
                      </SelectItem>
                      <SelectItem value="sekunder" className="text-xs">
                        🟠 Produksi Sekunder / Pengemasan (Packing, Labeling, Dus)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-1">
                <Label className="text-xs">Jabatan (Opsional)</Label>
                <SearchableCombobox
                  value={form.role}
                  onValueChange={(v) => set("role", v)}
                  options={uniqueRoles}
                  placeholder="Pilih atau ketik jabatan..."
                  searchPlaceholder="Cari jabatan..."
                  allowCustom={true}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Status Akun</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="z-[100]">
                    <SelectItem value="aktif">Aktif</SelectItem>
                    <SelectItem value="nonaktif">Nonaktif</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Status Karyawan</Label>
                <SearchableCombobox
                  value={form.employment_status || "Karyawan Tetap"}
                  onValueChange={(v) => set("employment_status", v)}
                  options={["Karyawan Tetap", "Karyawan Kontrak", "Karyawan Percobaan (Probation)", "Harian Lepas", "Magang (Internship)", "Freelance"]}
                  placeholder="Pilih status karyawan..."
                  searchPlaceholder="Cari status..."
                  allowCustom={true}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Jenis Kelamin</Label>
                <Select value={form.gender || "pria"} onValueChange={(v) => set("gender", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="z-[100]">
                    <SelectItem value="pria">Pria</SelectItem>
                    <SelectItem value="wanita">Wanita</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Tanggal Bergabung</Label>
                <CustomDatePicker value={form.join_date} onChange={(val) => set("join_date", val)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Tanggal Lahir</Label>
                <CustomDatePicker 
                  value={form.birth_date} 
                  onChange={(val) => set("birth_date", val)} 
                  fromYear={1950}
                  toYear={new Date().getFullYear()}
                  align="end"
                />
              </div>
              
              <div className="space-y-1 flex flex-col justify-center gap-1 pt-4">
                <Label className="text-xs">Face ID (Opsional)</Label>
                {form.face_descriptor ? (
                  <div className="flex items-center gap-2 text-emerald-600 text-xs font-bold bg-emerald-50 px-2 py-2 rounded-md border border-emerald-100 w-fit">
                    <CheckCircle2 className="w-4 h-4" /> Wajah Terekam
                    <Button variant="link" className="text-xs h-auto p-0 ml-2 text-indigo-600" onClick={() => setIsFaceModalOpen(true)}>Ubah</Button>
                  </div>
                ) : (
                  <Button variant="outline" size="sm" className="h-8 text-xs flex gap-2 w-full border-dashed border-indigo-200 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50" onClick={() => setIsFaceModalOpen(true)}>
                    <Camera className="w-3.5 h-3.5" /> Pindai Wajah
                  </Button>
                )}
              </div>

              <div className="space-y-1 flex items-center gap-4 pt-5 col-span-2">
                <div className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    id="require_attendance_add" 
                    className="w-4 h-4 rounded border-slate-300 text-[#8C6208] focus:ring-[#8C6208]"
                    checked={form.require_attendance !== false} 
                    onChange={(e) => set("require_attendance", e.target.checked)} 
                  />
                  <Label htmlFor="require_attendance_add" className="text-xs cursor-pointer">Masuk Daftar Absensi</Label>
                </div>
                <div className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    id="saturday_off_add" 
                    className="w-4 h-4 rounded border-slate-300 text-[#8C6208] focus:ring-[#8C6208]"
                    checked={form.saturday_off === true} 
                    onChange={(e) => set("saturday_off", e.target.checked)} 
                  />
                  <Label htmlFor="saturday_off_add" className="text-xs cursor-pointer">Sabtu Libur (Tidak Wajib Absen)</Label>
                </div>
              </div>
              <div className="space-y-1 col-span-2">
                <Label className="text-xs">Catatan</Label>
                <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} placeholder="Catatan tambahan..." />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={onClose}>Batal</Button>
              <Button onClick={handleSave} disabled={loading || !form.full_name || !form.email || !form.password}>
                {loading ? "Menyimpan..." : "Simpan"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <FaceRegistrationModal 
        isOpen={isFaceModalOpen}
        onClose={() => setIsFaceModalOpen(false)}
        onRegistered={async (descriptorString, base64Image) => {
          set("face_descriptor", descriptorString);
          if (base64Image) {
            try {
              const res = await fetch(base64Image);
              const blob = await res.blob();
              const file = new File([blob], `face_${Date.now()}.jpg`, { type: 'image/jpeg' });
              const { file_url } = await base44.integrations.Core.UploadFile({ file });
              if (file_url) set("face_photo_url", file_url);
            } catch (e) {
              console.warn("Upload face photo error:", e);
            }
          }
          setIsFaceModalOpen(false);
        }}
      />
    </>
  );
}